import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, LockKeyhole } from 'lucide-react';
import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import Landing from './Landing';

const inputCls = 'w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#ff2e2e] focus:outline-none focus:bg-white/10 transition-colors';

const AdminConsole = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, loginWithAdminNumber } = useAuth();
  const [adminNumber, setAdminNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!adminNumber || !password) {
      setError('Skriv inn adminnummer og passord.');
      return;
    }
    setLoading(true);
    try {
      const result = await loginWithAdminNumber(adminNumber, password);
      if (!result?.mfa_required) navigate('/admin_console', { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <div className="min-h-screen bg-black flex items-center justify-center text-white/60">Lastar…</div>;
  if (user?.is_admin) return <Landing adminPreview />;

  return (
    <div className="min-h-screen relative bg-black overflow-hidden" data-testid="admin-console-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,46,46,0.22),_transparent_35%)]" />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-[#120d18]" />

      <Link to="/login" className="absolute top-6 left-6 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm">
        <ArrowLeft size={16} /> Tilbake
      </Link>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size="md" /></div>

          <div className="rounded-3xl bg-[#0f0a17]/95 backdrop-blur-xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/50">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#ffb400]/15 text-[#ffb400] mb-4">
                <ShieldCheck size={24} />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-2" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
                Admin Console
              </h1>
              <p className="text-white/60">Logg inn med adminnummer og passord</p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="admin-console-form">
              <div>
                <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">Adminnummer</label>
                <div className="relative">
                  <ShieldCheck size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input data-testid="admin-number-input" type="text" value={adminNumber} onChange={(e) => setAdminNumber(e.target.value)} placeholder="RW27-A-001" className={inputCls} required />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">Passord</label>
                <div className="relative">
                  <LockKeyhole size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Skriv inn passordet ditt" className={inputCls} required />
                </div>
              </div>

              {error && <div data-testid="admin-console-error" className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>}

              <button type="submit" data-testid="admin-console-submit" disabled={loading} className="w-full py-4 rounded-full bg-[#ffb400] text-black font-bold uppercase tracking-wide hover:bg-[#ffca3a] transition-colors shadow-lg shadow-[#ffb400]/20 disabled:opacity-60">
                {loading ? 'Logger inn...' : 'Logg inn som admin'}
              </button>
            </form>
            <p className="text-white/45 text-sm text-center mt-5">
              Første gong? <Link to="/login" className="text-[#ffb400] font-semibold hover:underline">Logg inn på vanleg måte</Link>, opne Medlemsside og opprett adminpassord under Admininnlogging.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConsole;
